import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";

import { usePagination } from "hooks";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setContent,
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  TASK_STATUS_COMPLETED,
  TASK_STATUS_IN_PROGRESS,
  TASK_STATUS_TODO
} from "constants/staff-tasks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useGetPatientQuery } from "services/patients";
import {
  useCreateStaffTaskMutation,
  useDeleteStaffTaskMutation,
  useGetStaffTasksQuery,
  useQueryStaffTasksCounterQuery,
  useUpdateStaffTaskMutation
} from "services/staff-tasks";
import type { Patient } from "services/types/patients.type";
import { StaffTask, StaffTasksFilter } from "services/types/staff-tasks.type";

import { PATIENT_HEADER_QUERY } from "../use-patient.hook";
import { StaffTaskFormValues } from "./components/staff-task-form.component";
import { formatStaffTaskToInput } from "./components/staff-task.utils";

export function usePatientStaffTasksDrawerProvider() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const [isOpen, setIsOpen] = useState(false);
  const [editMode, setEditMode] = useState<StaffTask>();
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [patientId, setPatientId] = useState<number | undefined>(-1);
  const [isOrderByDueDate, setIsOrderByDueDate] = useState<boolean>(false);
  const [isPatientViewMode, setIsPatientViewMode] = useState(false);
  const [includeCreatedCompleted, setIncludeCreatedCompleted] = useState(false);

  const [deleteStaffTask] = useDeleteStaffTaskMutation();
  const [updateStaffTask, { isLoading: isUpdating }] =
    useUpdateStaffTaskMutation();

  const [createStaffTask, { isLoading: isCreating }] =
    useCreateStaffTaskMutation();

  const tasksSkipCondition = isPatientViewMode
    ? !patientId || patientId === -1
    : !currentUser?.id || currentUser?.id === -1;

  const {
    data: patient,
    refetch: refetchPatient,
    isUninitialized: isPatientUninitialized
  } = useGetPatientQuery(
    { patientId: Number(patientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !isPatientViewMode || !patientId || patientId === -1
    }
  );

  const {
    data: tasksAssignedToMe = [],
    fetchMore: fetchMoreAssignedToMe,
    hasMoreData: hasMoreAssignedToMe,
    refreshData: refetchUserTasks,
    isUninitialized: isUserTasksUnInitialized,
    isLoading: isFetchingTasksAssignedToMe
  } = usePagination<StaffTasksFilter, StaffTask>({
    useQuery: useGetStaffTasksQuery,
    limit: 25,
    params: {
      assignee: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_complete: false,
      is_order_by_due_at: isOrderByDueDate,
      status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    options: {
      skip: !isOpen || tasksSkipCondition
    }
  });

  const {
    data: tasksAssignedToMeCount = 0,
    refetch: refetchUserTasksCount,
    isUninitialized: isUserTasksCountUninitialized
  } = useQueryStaffTasksCounterQuery(
    {
      assignee: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_complete: false,
      is_order_by_due_at: isOrderByDueDate,
      status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    {
      skip: tasksSkipCondition
    }
  );

  const {
    data: tasksCreatedByMe = [],
    fetchMore: fetchMoreMyCreatedTasks,
    hasMoreData: hasMoreCreatedByMe,
    refreshData: refetchMyCreatedTasks,
    isUninitialized: isMyCreatedTasksUninitialized,
    isLoading: isFetchingTasksCreatedByMe
  } = usePagination<StaffTasksFilter, StaffTask>({
    useQuery: useGetStaffTasksQuery,
    limit: 25,
    params: {
      created_by: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_order_by_due_at: isOrderByDueDate,
      status: includeCreatedCompleted
        ? [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS, TASK_STATUS_COMPLETED]
        : [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    options: {
      skip: !isOpen || tasksSkipCondition
    }
  });

  const {
    data: tasksCreatedByMeCount = 0,
    refetch: refetchMyCreatedTasksCount,
    isUninitialized: isMyCreatedTasksCountUninitialized
  } = useQueryStaffTasksCounterQuery(
    {
      created_by: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_order_by_due_at: isOrderByDueDate,
      status: includeCreatedCompleted
        ? [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS, TASK_STATUS_COMPLETED]
        : [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    {
      skip: !isOpen || tasksSkipCondition
    }
  );

  const {
    data: myCompletedTasks = [],
    fetchMore: fetchMoreMyCompletedTasks,
    hasMoreData: hasMoreCompletedTasks,
    refreshData: refetchMyCompletedTasks,
    isUninitialized: isMyCompletedTasksUninitialized,
    isLoading: isFetchingMyCompletedTasks
  } = usePagination<StaffTasksFilter, StaffTask>({
    useQuery: useGetStaffTasksQuery,
    limit: 25,
    params: {
      assignee: currentUser.id,
      is_complete: true,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_order_by_due_at: isOrderByDueDate
    },
    options: {
      skip: !isOpen || tasksSkipCondition
    }
  });

  const {
    data: myCompletedTasksCount = 0,
    refetch: refetchMyCompletedTasksCount,
    isUninitialized: isMyCompletedTasksCountUninitialized
  } = useQueryStaffTasksCounterQuery(
    {
      created_by: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_complete: true,
      is_order_by_due_at: isOrderByDueDate
    },
    {
      skip: !isOpen || tasksSkipCondition
    }
  );

  const refetchTasks = useCallback(() => {
    if (!isUserTasksUnInitialized) {
      refetchUserTasks();
    }
    if (!isMyCreatedTasksUninitialized) {
      refetchMyCreatedTasks();
    }
    if (!isMyCompletedTasksUninitialized) {
      refetchMyCompletedTasks();
    }
  }, [
    isMyCreatedTasksUninitialized,
    isUserTasksUnInitialized,
    refetchMyCreatedTasks,
    refetchUserTasks,
    isMyCompletedTasksUninitialized,
    refetchMyCompletedTasks
  ]);

  const refetchTasksCounters = useCallback(() => {
    if (!isUserTasksCountUninitialized) {
      refetchUserTasksCount();
    }
    if (!isMyCreatedTasksCountUninitialized) {
      refetchMyCreatedTasksCount();
    }
    if (!isMyCompletedTasksCountUninitialized) {
      refetchMyCompletedTasksCount();
    }
  }, [
    isMyCreatedTasksCountUninitialized,
    isUserTasksCountUninitialized,
    refetchMyCreatedTasksCount,
    refetchUserTasksCount,
    isMyCompletedTasksCountUninitialized,
    refetchMyCompletedTasksCount
  ]);

  const refetchAll = useCallback(() => {
    refetchTasks();
    refetchTasksCounters();
    if (isPatientViewMode && !isPatientUninitialized) {
      refetchPatient();
    }
  }, [
    refetchTasks,
    isPatientUninitialized,
    refetchPatient,
    isPatientViewMode,
    refetchTasksCounters
  ]);

  const handleComplete = useCallback(
    (task: StaffTask) => {
      updateStaffTask({
        id: task.id,
        patient: Number(task.patient?.id),
        completed_at: task.completed_at
      }).then(() => {
        refetchAll();
      });
    },
    [updateStaffTask, refetchAll]
  );

  const handleDelete = useCallback(
    (task: StaffTask) => {
      dispatch(setTitle("Are you sure you want to delete the task?"));
      dispatch(
        setOnChange(() => {
          setPatientId(Number(task?.patient?.id));

          deleteStaffTask(task.id).then(() => {
            refetchAll();
          });
        })
      );
      dispatch(setOpen(true));
      dispatch(setContent(`Delete "${task.title}"?`));
    },
    [deleteStaffTask, refetchAll, dispatch]
  );

  const handleClose = useCallback(() => {
    setIsOpenCreate(false);
    setIsOpen(false);
    setEditMode(undefined);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setIsOpenCreate(false);
    setEditMode(undefined);
  }, []);

  const handleCreate = useCallback(
    (
      data: StaffTaskFormValues,
      callback?: (createdTaskId?: number) => void
    ) => {
      setPatientId(Number(data?.patient?.id));
      createStaffTask(formatStaffTaskToInput(data)).then((response) => {
        refetchAll();
        const createdTaskId =
          "data" in response ? response.data?.id : undefined;
        callback?.(createdTaskId);
        handleCloseCreate();
      });
    },
    [createStaffTask, handleCloseCreate, refetchAll]
  );

  const handleUpdate = useCallback(
    (data: StaffTaskFormValues) => {
      setPatientId(Number(data?.patient?.id));
      updateStaffTask(formatStaffTaskToInput(data)).then(() => {
        refetchAll();
        handleCloseCreate();
      });
    },
    [updateStaffTask, refetchAll, handleCloseCreate]
  );

  const handleOpen = useCallback((patientId?: number) => {
    if (typeof patientId === "number" && patientId !== -1) {
      setPatientId(Number(patientId));
      setIsPatientViewMode(true);
    } else {
      setIsPatientViewMode(false);
    }
    setIsOpen(true);
  }, []);

  const handleOpenCreate = useCallback((patientId?: number) => {
    if (typeof patientId === "number" && patientId !== -1) {
      setPatientId(Number(patientId));
      setIsPatientViewMode(true);
    } else {
      setIsPatientViewMode(false);
    }
    setIsOpenCreate(true);
    setIsOpen(true);
    setEditMode(undefined);
  }, []);

  const handleEdit = useCallback((task: StaffTask) => {
    setIsOpenCreate(true);
    setIsOpen(true);
    setEditMode(task);
  }, []);

  return {
    isOpen,
    isOpenCreate,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    handleComplete,
    handleEdit,
    editMode,
    isFetching:
      isFetchingTasksAssignedToMe ||
      isFetchingTasksCreatedByMe ||
      isFetchingMyCompletedTasks,
    isUninitialized:
      isMyCreatedTasksUninitialized ||
      isUserTasksUnInitialized ||
      isMyCompletedTasksUninitialized,
    patient:
      !isPatientViewMode || !patientId || patientId === -1
        ? undefined
        : patient,
    handleCreate,
    isCreating,
    handleUpdate,
    isUpdating,
    isPatientViewMode,
    isOrderByDueDate,
    setIsOrderByDueDate,
    setIsPatientViewMode,
    tasksAssignedToMe,
    tasksCreatedByMe,
    myCompletedTasks,
    tasksAssignedToMeCount,
    tasksCreatedByMeCount,
    myCompletedTasksCount,
    fetchMoreMyCompletedTasks,
    fetchMoreAssignedToMe,
    fetchMoreMyCreatedTasks,
    hasMoreAssignedToMe,
    hasMoreCreatedByMe,
    hasMoreCompletedTasks,
    refetchAll,
    includeCreatedCompleted,
    setIncludeCreatedCompleted
  };
}

export const PatientStaffTasksDrawerContext = createContext<{
  isOpenCreate: boolean;
  isOpen: boolean;
  tasksAssignedToMe: StaffTask[];
  tasksCreatedByMe: StaffTask[];
  myCompletedTasks: StaffTask[];
  tasksAssignedToMeCount: number;
  tasksCreatedByMeCount: number;
  myCompletedTasksCount: number;
  fetchMoreMyCompletedTasks: () => void;
  fetchMoreAssignedToMe: () => void;
  fetchMoreMyCreatedTasks: () => void;
  hasMoreAssignedToMe: boolean;
  hasMoreCreatedByMe: boolean;
  hasMoreCompletedTasks: boolean;
  isFetching: boolean;
  isUninitialized: boolean;
  handleOpen: (patientId?: number) => void;
  handleOpenCreate: (patientId?: number) => void;
  handleClose: () => void;
  handleCloseCreate: () => void;
  handleDelete: (task: StaffTask) => void;
  handleComplete: (task: StaffTask) => void;
  handleEdit: (task: StaffTask) => void;
  editMode?: StaffTask;
  patient?: Patient;
  handleCreate: (
    data: StaffTaskFormValues,
    callback?: (createdTaskId?: number) => void
  ) => void;
  isCreating: boolean;
  handleUpdate: (data: StaffTaskFormValues) => void;
  isUpdating: boolean;
  isPatientViewMode: boolean;
  isOrderByDueDate: boolean;
  setIsOrderByDueDate: React.Dispatch<React.SetStateAction<boolean>>;
  setPatientId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setIsPatientViewMode: React.Dispatch<React.SetStateAction<boolean>>;
  refetchAll: () => void;
  includeCreatedCompleted: boolean;
  setIncludeCreatedCompleted: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

type PatientDrawerProviderProps = {
  children?: ReactNode;
};

export const PatientStaffTasksDrawerProvider = ({
  children
}: PatientDrawerProviderProps) => {
  const {
    isOpen,
    isOpenCreate,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    handleComplete,
    handleEdit,
    editMode,
    patient,
    handleCreate,
    isCreating,
    handleUpdate,
    isUpdating,
    isPatientViewMode,
    isOrderByDueDate,
    setIsOrderByDueDate,
    tasksAssignedToMe,
    tasksCreatedByMe,
    myCompletedTasks,
    tasksAssignedToMeCount,
    tasksCreatedByMeCount,
    myCompletedTasksCount,
    fetchMoreMyCompletedTasks,
    fetchMoreAssignedToMe,
    fetchMoreMyCreatedTasks,
    hasMoreAssignedToMe,
    hasMoreCreatedByMe,
    hasMoreCompletedTasks,
    isFetching,
    isUninitialized,
    setIsPatientViewMode,
    refetchAll,
    includeCreatedCompleted,
    setIncludeCreatedCompleted
  } = usePatientStaffTasksDrawerProvider();

  const value = useMemo(
    () => ({
      isOpen,
      isOpenCreate,
      handleOpen,
      handleOpenCreate,
      handleClose,
      handleCloseCreate,
      handleDelete,
      setPatientId,
      setIsPatientViewMode,
      handleComplete,
      handleEdit,
      editMode,
      patient,
      handleCreate,
      isCreating,
      handleUpdate,
      isUpdating,
      isPatientViewMode,
      isOrderByDueDate,
      setIsOrderByDueDate,
      tasksAssignedToMe,
      tasksCreatedByMe,
      myCompletedTasks,
      tasksAssignedToMeCount,
      tasksCreatedByMeCount,
      myCompletedTasksCount,
      fetchMoreMyCompletedTasks,
      fetchMoreAssignedToMe,
      fetchMoreMyCreatedTasks,
      hasMoreAssignedToMe,
      hasMoreCreatedByMe,
      hasMoreCompletedTasks,
      isFetching,
      isUninitialized,
      refetchAll,
      includeCreatedCompleted,
      setIncludeCreatedCompleted
    }),
    [
      isOpen,
      isOpenCreate,
      handleOpen,
      handleOpenCreate,
      handleClose,
      handleCloseCreate,
      handleDelete,
      setPatientId,
      setIsPatientViewMode,
      handleComplete,
      handleEdit,
      editMode,
      patient,
      handleCreate,
      isCreating,
      handleUpdate,
      isUpdating,
      isPatientViewMode,
      isOrderByDueDate,
      setIsOrderByDueDate,
      tasksAssignedToMe,
      tasksCreatedByMe,
      myCompletedTasks,
      tasksAssignedToMeCount,
      tasksCreatedByMeCount,
      myCompletedTasksCount,
      fetchMoreMyCompletedTasks,
      fetchMoreAssignedToMe,
      fetchMoreMyCreatedTasks,
      hasMoreAssignedToMe,
      hasMoreCreatedByMe,
      hasMoreCompletedTasks,
      isFetching,
      isUninitialized,
      refetchAll,
      includeCreatedCompleted,
      setIncludeCreatedCompleted
    ]
  );

  return (
    <PatientStaffTasksDrawerContext.Provider value={value}>
      {children}
    </PatientStaffTasksDrawerContext.Provider>
  );
};

export const usePatientStaffTasksDrawer = () => {
  const context = useContext(PatientStaffTasksDrawerContext);
  if (!context) {
    throw new Error(
      `usePatientStaffTasksDrawer must be used within a PatientStaffTasksDrawerContext`
    );
  }

  const {
    isOpen,
    isOpenCreate,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    setIsPatientViewMode,
    handleComplete,
    handleEdit,
    editMode,
    patient,
    handleCreate,
    isCreating,
    handleUpdate,
    isUpdating,
    isPatientViewMode,
    isOrderByDueDate,
    setIsOrderByDueDate,
    tasksAssignedToMe,
    tasksCreatedByMe,
    myCompletedTasks,
    tasksAssignedToMeCount,
    tasksCreatedByMeCount,
    myCompletedTasksCount,
    fetchMoreMyCompletedTasks,
    fetchMoreAssignedToMe,
    fetchMoreMyCreatedTasks,
    hasMoreAssignedToMe,
    hasMoreCreatedByMe,
    hasMoreCompletedTasks,
    isFetching,
    isUninitialized,
    refetchAll,
    includeCreatedCompleted,
    setIncludeCreatedCompleted
  } = context;

  return {
    isOpen,
    isOpenCreate,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    handleComplete,
    handleEdit,
    editMode,
    setPatientId,
    setIsPatientViewMode,
    patient,
    handleCreate,
    isCreating,
    handleUpdate,
    isUpdating,
    isPatientViewMode,
    isOrderByDueDate,
    setIsOrderByDueDate,
    tasksAssignedToMe,
    tasksCreatedByMe,
    myCompletedTasks,
    tasksAssignedToMeCount,
    tasksCreatedByMeCount,
    myCompletedTasksCount,
    fetchMoreMyCompletedTasks,
    fetchMoreAssignedToMe,
    fetchMoreMyCreatedTasks,
    hasMoreAssignedToMe,
    hasMoreCreatedByMe,
    hasMoreCompletedTasks,
    isFetching,
    isUninitialized,
    refetchAll,
    includeCreatedCompleted,
    setIncludeCreatedCompleted
  };
};
